<template>
    <div class="account-cards">
        <div class="create-container">
            <button class="btn" @click="showCreatePopup">{{ $t('ACCOUNT_CARDS__CREATE') }}</button>
            <button 
                class="btn" 
                @click="showCreateAccountPopup" 
                v-if="isSupervisor"
            >{{ $t('ACCOUNT_CARDS__CREATE_USER') }}</button>
            <button
                class="btn btn-filter"
                @click="filterFormVisible = !filterFormVisible"
            >{{ $t('ACCOUNT_CARDS__SEARCH') }}</button>
            <button
                class="btn"
                @click="lockUnlockFormVisible = true"
                v-if="shop"
            >{{ $t('ACCOUNT_CARDS__LOCK_UNLOCK') }}</button>
            <button
                class="btn"
                @click="assignCardVisible = true"
                v-if="shop"
            >{{ $t('ACCOUNT_CARDS__ASSIGN') }}</button>
            <button
                class="btn"
                @click="activateCardVisible = true"
                v-if="shop"
            >{{ $t('ACCOUNT_CARDS__ACTIVATE') }}</button>
            <button class="btn export" v-if="dataCount > 0" @click="exportMain">
                <span>{{ $t('ACCOUNT_CARDS__EXPORT') }}</span>
                <i class="material-icons">cloud_download</i>
            </button>
        </div>
        <transition name="filter-show">
            <form class="filter-container" @submit.prevent="search" v-show="filterFormVisible">
                <custom-input
                    v-model="requestParams.FilterString"
                    :title="$t('ACCOUNT_CARDS__FILTER')"
                    helperText="* Search Any Keyword"
                    @clear="requestParams.FilterString = ''"
                />
                <custom-select
                    v-model="requestParams.WebShopId"
                    :title="$t('ACCOUNT_CARDS__WEB_SHOP')"
                    :options="webshopOptions"
                    @clear="requestParams.WebShopId = ''"
                    v-if="shop"
                ></custom-select>
                <custom-input
                    :title="$t('ACCOUNT_CARDS__FROM_ID')"
                    inputType="number"
                    v-model="requestParams.FromExternalId"
                    @clear="requestParams.FromExternalId = ''"
                ></custom-input>
                <custom-input
                    :title="$t('ACCOUNT_CARDS__TO_ID')"
                    inputType="number"
                    v-model="requestParams.ToExternalId"
                    @clear="requestParams.ToExternalId = ''"
                ></custom-input>
                <three-way-checkbox
                    v-model="requestParams.Locked"
                    :title="$t('ACCOUNT_CARDS__LOCKED')"
                ></three-way-checkbox>
                <three-way-checkbox
                    v-model="requestParams.Assigned"
                    :title="$t('ACCOUNT_CARDS__ASSIGNED')"
                    v-if="shop"
                ></three-way-checkbox>
                <three-way-checkbox
                    v-model="requestParams.Activated"
                    :title="$t('ACCOUNT_CARDS__ACTIVATED')"
                ></three-way-checkbox>
                <button class="btn search">{{ $t('ACCOUNT_CARDS__SEARCH') }}</button>
            </form>
        </transition>
        <div class="general-cards-grid">
            <grid
                :fullGrid="true"
                :passedData="gridData"
                identificator="id"
                :columns="columns"
                :passedCount="dataCount"
                :passedPageSize="15"
                :title="$t('ACCOUNT_CARDS__ACCOUNT_CARDS')"
                @pageChanged="pageChangeHandler"
                @trClicked="trClickHandler"
                @sort="sortHandler"
                :lastRow="lastRow"
            ></grid>
        </div>
        <overlay :visible="createFormVisible" @close="createFormVisible = false">
            <popup
                :title="$t('ACCOUNT_CARDS__CREATE_ACCOUNT_CARDS')"
                :expanded="expandedCreationPopup"
                @close="createFormVisible = false"
            >
                <form class="create-form main-form" @submit.prevent="createCards">
                    <custom-input
                        v-if="shop"
                        v-model="createCardFormControls.quantity.value"
                        :errorMessage="createCardFormControls.quantity.errorMessage"
                        helperText="Required / Max 1000"
                        :title="$t('ACCOUNT_CARDS__QUANTITY')"
                        inputType="number"
                        :isError="createCardFormControls.quantity.isError"
                        @clear="createCardFormControls.quantity.value = ''"
                    ></custom-input>
                    <custom-select
                        v-model="createCardFormControls.balance"
                        :title="$t('ACCOUNT_CARDS__BALANCE')"
                        :options="balanceOptions"
                        @clear="createCardFormControls.balance = '0'"
                    ></custom-select>
                    <custom-select
                        v-if="shop"
                        v-model="createCardFormControls.webshop"
                        :title="$t('ACCOUNT_CARDS__WEB_SHOP')"
                        :options="webshopOptions"
                        @clear="createCardFormControls.webshop = ''"
                    ></custom-select>
                    <custom-input
                        v-if="!shop"
                        v-model="createCardFormControls.userName"
                        :title="$t('ACCOUNT_CARDS__USERNAME')"
                        @clear="createCardFormControls.userName = ''"
                    />
                    <custom-input
                        v-model="createCardFormControls.password"
                        :title="$t('MOBILE_DASHBOARD__PASSWORD')"
                        @clear="createCardFormControls.password = ''"
                        helperText="* Min 3 letters"
                    />
                    <switcher
                        v-if="shop"
                        v-model="createCardFormControls.active"
                        :title="$t('ACCOUNT_CARDS__ACTIVE')"
                    ></switcher>
                    <button class="btn">{{ $t('ACCOUNT_CARDS__CREATE_CARDS') }}</button>
                </form>
                <loader v-if="loading"></loader>
                <grid
                    v-if="newlyAddedCardsList.length > 0"
                    :fullGrid="true"
                    :passedData="newlyAddedCardsList"
                    identificator="id"
                    :columns="columnsWithoutActions"
                    :passedCount="newlyAddedCardsCount"
                    :passedPageSize="10"
                    :title="$t('ACCOUNT_CARDS__CREATE_CARDS')"
                    @pageChanged="newlyAddedCardsPageChangeHandler"
                    @trClicked="trClickHandler"
                ></grid>
                <div
                    class="btn export"
                    v-if="newlyAddedCardsList.length > 0"
                    @click="exportNewlyAddedToExcel"
                >
                    <span>{{ $t('ACCOUNT_CARDS__EXPORT') }}</span>
                    <i class="material-icons">cloud_download</i>
                </div>
            </popup>
        </overlay>
        <overlay :visible="createUserPopupVisible" @close="createUserPopupVisible = false">
            <popup
                :title="$t('ACCOUNT_CARDS__CREATE_USER')"
                @close="createUserPopupVisible = false"
            >
                <form class="create-form main-form" @submit.prevent="createUser">
                    <custom-select
                        v-model="createUserFormControls.balance"
                        :title="$t('ACCOUNT_CARDS__BALANCE')"
                        :options="balanceOptions"
                        @clear="createUserFormControls.balance = '0'"
                    ></custom-select>
                    <custom-select
                        v-model="createUserFormControls.webshop"
                        :title="$t('ACCOUNT_CARDS__WEB_SHOP')"
                        :options="webshopOptions"
                        @clear="createUserFormControls.webshop = ''"
                    ></custom-select>
                    <custom-input
                        v-model="createUserFormControls.userName"
                        :title="$t('ACCOUNT_CARDS__USERNAME')"
                        @clear="createUserFormControls.userName = ''"
                    />
                    <custom-input
                        v-model="createUserFormControls.password"
                        :title="$t('ACCOUNT_CARDS__PASSWORD')"
                        @clear="createUserFormControls.password = ''"
                        helperText="* Min 3 letters"
                    />
                    <button class="btn">{{ $t('ACCOUNT_CARDS__CREATE_USER') }}</button>
                </form>
            </popup>
        </overlay>
        <overlay :visible="createdCardInfoVisible" @close="createdCardInfoVisible = false">
            <popup
                :title="$t('ACCOUNT_CARDS__CREATED_CARD_INFO')"
                class="card-info"
                @close="createdCardInfoVisible = false"
            >
                <ul>
                    <li>
                        <span>{{ $t('ACCOUNT_CARDS__USERNAME') + ' :' }}</span>
                        <span>{{ createdCardInfo.userName }}</span>
                    </li>
                    <li>
                        <span>{{ $t('ACCOUNT_CARDS__PASSWORD') + ' :' }}</span>
                        <span>{{ createdCardInfo.password }}</span>
                    </li>
                </ul>
            </popup>
        </overlay>
        <overlay :visible="lockUnlockFormVisible" @close="lockUnlockFormVisible = false">
            <popup
                :title="$t('ACCOUNT_CARDS__LOCK_UNLOCK_CARDS')"
                :expanded="lockUnlockPopupExpanded"
                @close="lockUnlockFormVisible = false"
            >
                <lock-unlock-account-cards
                    :columns="columnsWithoutActions"
                    :webshopOptions="webshopOptions"
                    @expand="lockUnlockPopupExpanded = true"
                />
            </popup>
        </overlay>
        <overlay :visible="editCardPopupVisible" @close="editCardPopupVisible = false">
            <popup :title="$t('ACCOUNT_CARDS__ACTIONS')" expanded @close="editCardPopupVisible = false">
                <edit-card v-if="selectedCard != null" :card="selectedCard" @update="getData"/>
            </popup>
        </overlay>
        <overlay :visible="assignCardVisible" @close="assignCardVisible = false">
            <popup
                :title="$t('ACCOUNT_CARDS__ASSIGN_CARDS')"
                :expanded="assignCardPopupExpanded"
                @close="assignCardVisible = false"
            >
                <assign-card
                    :columns="columnsWithoutActions"
                    :webShopOptions="webshopOptions"
                    @expand="assignCardPopupExpanded = true"
                />
            </popup>
        </overlay>
        <overlay :visible="activateCardVisible" @close="activateCardVisible = false">
            <popup
                :title="$t('ACCOUNT_CARDS__ACTIVATE_CARDS')"
                :expanded="activateCardPopupExpanded"
                @close="activateCardVisible = false"
            >
                <activate-card
                    :columns="columnsWithoutActions"
                    :webShopOptions="webshopOptions"
                    activateCardsURL="WebShop/ActivateAccountCards"
                    @expand="activateCardPopupExpanded = true"
                />
            </popup>
        </overlay>
        <custom-snackbar
            :purpose="mainSnackbar.purpose"
            :text="mainSnackbar.text"
            v-if="mainSnackbar.visible"
            @close="mainSnackbar.visible = false"
        />
    </div>
</template>

<script>
import axios from '@/Helpers/AxiosInstance.js'
import Grid from '@/components/ReusableComponents/Grid/Grid.vue'
import Overlay from '@/components/ReusableComponents/Overlay/Overlay.vue'
import CustomInput from '@/components/ReusableComponents/CustomInput/CustomInput.vue'
import CustomSelect from '@/components/ReusableComponents/CustomSelect/CustomSelect.vue'
import Loader from '@/components/ReusableComponents/Loader/Loader.vue'
import Switcher from '@/components/ReusableComponents/Switcher/Switcher.vue'
import ThreeWayCheckbox from '@/components/ReusableComponents/ThreeWayCheckbox/ThreeWayCheckbox.vue'
import CustomSnackbar from '@/components/ReusableComponents/CustomSnackbar/CustomSnackbar.vue'
import Popup from '@/components/ReusableComponents/Popup/Popup.vue'
import LockUnlockAccountCards from './LockUnlockAccountCards.vue'
import EditCard from './EditCard.vue'
import AssignCard from './AssignCard.vue'
import ActivateCard from './ActivateCard.vue'
import EventBus from '@/Helpers/EventBus.js'
export default {
    components: {
        Grid,
        Overlay,
        CustomInput,
        CustomSelect,
        Loader,
        Switcher,
        ThreeWayCheckbox,
        LockUnlockAccountCards,
        Popup,
        EditCard,
        AssignCard,
        ActivateCard,
        CustomSnackbar,
    },
    created() {
        this.getData()
        this.changeFilterValuesIfNotSupervisor()
    },
    mounted() {
        //Deposit click inside the grid
        this.$el.addEventListener('click', e => {
            if (e.target.matches('.grid__edit-balance .btn')) {
                var input = e.target.previousElementSibling.querySelector('input')
                var amount = input.value
                var text = 'Depositted Successfully'
                if(e.target.parentNode.classList.contains('withdraw')) {
                    text = 'Withdrawed Successfully'
                    amount = amount * -1
                }
                if (!amount) return
                var elementID = e.target.closest('tr').getAttribute('rowid')
                axios
                    .post('WebShop/EditUserBalance', {
                        cardId: elementID,
                        amount: amount,
                        allowNegativeBalance: false,
                    })
                    .then(resp => {
                        if (resp.data.status == 1) {
                            //success
                            this.mainSnackbar.text = text
                            this.mainSnackbar.purpose = 'success'
                            this.mainSnackbar.visible = 'true'
                            input.value = ''
                            this.getData()
                            EventBus.$emit('updateBalance')
                        } else {
                            this.showDepositError(resp.data.status)
                        }
                    })
                    .catch(() => {
                        this.showDepositError()
                    })
            }
        })
    },
    data() {
        return {
            gridData: [],
            //parameters for the main grid
            requestParams: {
                FromInternalId: '',
                ToInternalId: '',
                Assigned: null,
                Activated: null,
                Locked: null,
                WebShopId: '',
                FromExternalId: '',
                ToExternalId: '',
                FilterString: '',
                OrderColumn: '',
                OrderDirection: '',
                CurrentPage: 1,
                PageSize: 15,
            },
            dataCount: 0,
            createFormVisible: false,
            createCardFormControls: {
                quantity: {
                    value: '',
                    errorMessage: 'Please Enter Quantity',
                    isError: false,
                },
                balance: '0',
                webshop: '',
                active: false,
                userName: '',
                password: '',
            },
            createUserFormControls: {
                quantity: 1,
                active: true,
                balance: '0',
                webshop: '',
                userName: '',
                password: '',
            },
            createUserPopupVisible: false,
            createdCardInfoVisible: false,
            createdCardInfo: {},

            loading: false,
            expandedCreationPopup: false,
            fromInternalID: '',
            toInternalID: '',
            newlyAddedCardsList: [],
            //parameters to search newly created cards
            newlyAddedCardsRequestParams: {
                FromInternalId: '',
                ToInternalId: '',
                Assigned: '',
                Activated: '',
                Locked: '',
                WebShopId: '',
                FromExternalId: '',
                ToExternalId: '',
                OrderColumn: '',
                OrderDirection: '',
                CurrentPage: 1,
                PageSize: 10,
            },
            newlyAddedCardsCount: 0,
            filterFormVisible: false,
            lockUnlockFormVisible: false,
            lockUnlockPopupExpanded: false,
            editCardPopupVisible: false,
            selectedCard: null,
            assignCardVisible: false,
            assignCardPopupExpanded: false,
            activateCardVisible: false,
            activateCardPopupExpanded: false,
            mainSnackbar: {
                visible: false,
                text: '',
                purpose: 'success',
            },
            balanceOptions: [
                {
                    title: '0',
                    value: '0',
                },
                {
                    title: '5',
                    value: '5',
                },
                {
                    title: '10',
                    value: '10',
                },
                {
                    title: '20',
                    value: '20',
                },
                {
                    title: '50',
                    value: '50',
                },
                {
                    title: '100',
                    value: '100',
                },
            ],
        }
    },
    methods: {
        search() {
            this.requestParams.CurrentPage = 1
            this.getData()
        },
        //get main grid data
        getData() {
            axios
                .get('WebShop/FilterAccountCards', {
                    params: { ...this.requestParams },
                })
                .then(resp => {
                    this.gridData = resp.data.response
                    this.dataCount =
                        resp.data.response.length > 0
                            ? resp.data.response[0].count
                            : 0
                })
        },
        pageChangeHandler(page) {
            this.requestParams.CurrentPage = page
            this.getData()
        },
        newlyAddedCardsPageChangeHandler(page) {
            this.newlyAddedCardsRequestParams.CurrentPage = page
            this.getNewlyAddedList()
        },
        createCards() {
            if (this.shop) {
                var canSubmit = this.checkErrors(this.createCardFormControls)
                if (!canSubmit) return
                this.loading = true
                axios
                    .post('WebShop/CreateAccountCards', {
                        quantity: this.createCardFormControls.quantity.value,
                        balance: this.createCardFormControls.balance,
                        webShopId: this.createCardFormControls.webshop,
                        active: this.createCardFormControls.active,
                    })
                    .then(resp => {
                        if (resp.data.status == 1) {
                            this.loading = false
                            this.expandedCreationPopup = true
                            this.newlyAddedCardsRequestParams.FromInternalId =
                                resp.data.response.minId
                            this.newlyAddedCardsRequestParams.ToInternalId =
                                resp.data.response.maxId
                            this.getNewlyAddedList()
                        } else {
                            this.showAccountCreationError(resp.data.status)
                        }
                    })
                    .catch(() => this.showAccountCreationError())
            } else {
                let data = {
                    balance: this.createCardFormControls.balance,
                }
                if (this.createCardFormControls.userName) {
                    data['userName'] = this.createCardFormControls.userName
                }
                if(this.createCardFormControls.password) {
                    data['password'] = this.createCardFormControls.password
                }

                axios
                    .post('WebShop/CreateCustomerFromWebShop', data)
                    .then(resp => {
                        this.createFormVisible = false
                        this.mainSnackbar.visible = true
                        if (resp.data.status == 1) {
                            this.mainSnackbar.text = 'Success'
                            this.mainSnackbar.purpose = 'success'

                            this.createdCardInfo = resp.data.response
                            this.createdCardInfoVisible = true

                            this.getData()
                        } else {
                            this.mainSnackbar.purpose = 'error'
                            var code = this.statusCodes.filter(
                                e => e.key == resp.data.status
                            )[0]
                            if (code) this.mainSnackbar.text = this.$t('STATUS-CODE__' + code.value)
                        }
                    })
                    .catch(() => {
                        this.createFormVisible = false
                        this.mainSnackbar.visible = true
                        this.mainSnackbar.purpose = 'error'
                        this.mainSnackbar.text = 'An Error Has Occurred!'
                    })
            }
        },
        createUser() {
            var data = {
                balance: this.createUserFormControls.balance,
                webShopId: this.createUserFormControls.webshop,
                quantity: this.createUserFormControls.quantity,
                active: this.createUserFormControls.active,
            }
            if (this.createUserFormControls.userName) {
                data['userName'] = this.createUserFormControls.userName
            }
            if(this.createUserFormControls.password) {
                data['password'] = this.createUserFormControls.password
            }

            axios
                .post('WebShop/CreateCustomerFromShop', data)
                .then(resp => {
                    this.createUserPopupVisible = false
                    this.mainSnackbar.visible = true
                    if (resp.data.status == 1) {
                        this.mainSnackbar.text = 'Success'
                        this.mainSnackbar.purpose = 'success'

                        this.createdCardInfo = resp.data.response
                        this.createdCardInfoVisible = true
                    } else {
                        this.mainSnackbar.purpose = 'error'
                        var code = this.statusCodes.filter(
                            e => e.key == resp.data.status
                        )[0]
                        if (code) this.mainSnackbar.text = this.$t('STATUS-CODE__' + code.value)
                    }
                })
                .catch(() => {
                    this.createUserPopupVisible = false
                    this.mainSnackbar.visible = true
                    this.mainSnackbar.purpose = 'error'
                    this.mainSnackbar.text = 'An Error Has Occurred!'
                })
        },
        clearCreateForm() {
            this.createCardFormControls.quantity.isError = false
            this.createCardFormControls.quantity.errorMessage = ''
            this.createCardFormControls.quantity.value = ''
            this.createCardFormControls.balance = ''
            this.createCardFormControls.webshop = ''
            this.createCardFormControls.active = false
        },
        checkErrors(form) {
            if (form.quantity.value > 1000) {
                form.quantity.errorMessage = 'Maximum 1000'
                form.quantity.isError = true
                return false
            } else if (form.quantity.value == '') {
                form.quantity.errorMessage = 'Required'
                form.quantity.isError = true
                return false
            }
            form.quantity.isError = false
            return true
        },
        getNewlyAddedList() {
            axios
                .get('WebShop/FilterAccountCards', {
                    params: this.newlyAddedCardsRequestParams,
                })
                .then(resp => {
                    this.newlyAddedCardsList = resp.data.response
                    this.newlyAddedCardsCount =
                        resp.data.response.length > 0
                            ? resp.data.response[0].count
                            : 0
                })
        },
        exportNewlyAddedToExcel() {
            axios
                .get('WebShop/FilterAccountCards', {
                    params: {
                        ...this.newlyAddedCardsRequestParams,
                        GenerateExcelFile: true,
                    },
                    responseType: 'blob',
                })
                .then(response => {
                    const url = window.URL.createObjectURL(
                        new Blob([response.data])
                    )
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', 'CreatedCards.xlsx')
                    document.body.appendChild(link)
                    link.click()
                })
        },
        //export main grid data
        exportMain() {
            axios
                .get('WebShop/FilterAccountCards', {
                    params: {
                        ...this.requestParams,
                        GenerateExcelFile: true,
                    },
                    responseType: 'blob',
                })
                .then(response => {
                    const url = window.URL.createObjectURL(
                        new Blob([response.data])
                    )
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', 'CreatedCards.xlsx')
                    document.body.appendChild(link)
                    link.click()
                })
        },
        trClickHandler(element, e) {
            if (e.target.matches('.switcher.activate')) {
                axios
                    .post('WebShop/ActivateAccountCards', {
                        externalIdFrom: element.externalId,
                        externalIdTo: element.externalId,
                    })
                    .then(resp => {
                        if (resp.data.status == 1) {
                            e.target.classList += ' active'
                            this.getData()
                            this.mainSnackbar.purpose = 'success'
                            this.mainSnackbar.text = 'Successfully Activated!'
                            this.mainSnackbar.visible = true
                        } else {
                            this.showAccountActivationError(resp.data.status)
                        }
                    })
                    .catch(() => this.showAccountActivationError())
            }
            if (e.target.matches('.switcher.lock')) {
                let lock = !e.target.classList.contains('active')
                axios
                    .post('WebShop/LockUnlockAccountCards', {
                        webShopId: element.webShopId,
                        externalIdFrom: element.externalId,
                        externalIdTo: element.externalId,
                        unusedOnly: false,
                        locked: lock,
                    })
                    .then(resp => {
                        if (resp.data.status == 1) {
                            this.mainSnackbar.purpose = 'success'
                            this.mainSnackbar.text = lock ? 'Successfully Locked!' : 'Successfully Unlocked!'
                            this.mainSnackbar.visible = true
                            if(lock) e.target.classList.add('active')
                            else e.target.classList.remove('active')
                        }
                        else {
                            this.showLockError(resp.data.status)
                        }
                    })
                    .catch(() => this.showLockError())
            }
            if (e.target.matches('.btn.actions')) {
                if (!element.webShopId) return
                this.editCardPopupVisible = true
                this.selectedCard = element
            }
            if (e.target.matches('.btn.view-bonus')) {
                let id = element.id
                this.$router.push({ path: `/Bonuses`, query: { cardID: id }})
            }
        },
        sortHandler(data) {
            this.requestParams.OrderColumn = data.columnName
            this.requestParams.OrderDirection = data.orderByDesc ? 'desc' : 'asc'
            this.getData()
        },
        showDepositError(status) {
            this.mainSnackbar.visible = true
            this.mainSnackbar.purpose = 'error'
            this.mainSnackbar.text = 'An Error Occurred While Depositing!'
            var code = this.statusCodes.filter(e => e.key == status)[0]
            if (code) this.mainSnackbar.text = this.$t('STATUS-CODE__' + code.value)
        },
        showAccountCreationError(status) {
            this.createFormVisible = false
            this.mainSnackbar.visible = true
            this.mainSnackbar.purpose = 'error'
            this.mainSnackbar.text = 'An Error Occurred While Creating Card(s)!'
            var code = this.statusCodes.filter(e => e.key == status)[0]
            if (code) this.mainSnackbar.text = this.$t('STATUS-CODE__' + code.value)
        },
        showAccountActivationError(status) {
            this.activateCardVisible = false
            this.mainSnackbar.purpose = 'error'
            this.mainSnackbar.text = 'An Error Occurred While Activating!'
            this.mainSnackbar.visible = true
            var code = this.statusCodes.filter(e => e.key == status)[0]
            if (code) this.mainSnackbar.text = this.$t('STATUS-CODE__' + code.value)
        },
        showLockError(status) {
            this.activateCardVisible = false
            this.mainSnackbar.purpose = 'error'
            this.mainSnackbar.text = 'An Error Occurred While Locking / Unlocking!'
            this.mainSnackbar.visible = true
            var code = this.statusCodes.filter(e => e.key == status)[0]
            if (code) this.mainSnackbar.text = this.$t('STATUS-CODE__' + code.value)
        },
        changeFilterValuesIfNotSupervisor() {
            if (!this.shop && this.webshops.length > 0) {
                this.requestParams.Assigned = true
                this.WebShopId = this.webshops[0].id
            }
        },
        showCreatePopup() {
            this.createCardFormControls.quantity.value = ''
            this.createCardFormControls.balance = '0'
            this.createCardFormControls.webshop = ''
            this.createCardFormControls.active = false
            this.newlyAddedCardsList = []
            this.expandedCreationPopup = false
            this.createFormVisible = true
        },
        showCreateAccountPopup() {
            this.createUserFormControls.balance = '0'
            this.createUserFormControls.webshop = ''
            this.createUserFormControls.userName = ''
            this.createUserFormControls.password = ''
            this.createUserPopupVisible = true
        }
    },
    computed: {
        webshops() {
            return this.$store.state.webshops
        },
        webshopOptions() {
            var options = []
            this.webshops.forEach(shop => {
                let title = shop.name
                    ? `${shop.calculatedName} (${shop.name})`
                    : shop.calculatedName
                options.push({ title: title, value: shop.id + '' })
            })
            return options
        },
        shop() {
            return this.$store.state.shop
        },
        columns() {
            // if (this.webshops.length == 0) return []
            return [
                {
                    title: this.$t('ACCOUNT_CARDS__NAME'),
                    key: 'userName',
                    customTemplate: (item) => {
                        let calculatedName = item.calculatedName
                        let username = item.userName
                        let nickName = item.nickName
                        if(!calculatedName) {
                            if(username && nickName) return `(${username}, ${nickName})`
                            if(username) return username
                            if(nickName) return nickName
                        }
                        if(username && username.toLowerCase() != calculatedName.toLowerCase() && nickName && nickName.toLowerCase() != username.toLowerCase()) {
                            return `${calculatedName}(${username}, ${nickName})`
                        }
                        else if(username && username.toLowerCase() != calculatedName.toLowerCase()) {
                            return `${calculatedName}(${username})`
                        }
                        else if(nickName) {
                            return `${calculatedName}(${nickName})`
                        }
                        return calculatedName
                    },
                },
                {
                    title: this.$t('ACCOUNT_CARDS__BALANCE'),
                    key: 'balanceDecimal',
                    customTemplate: (item, val) => {
                        return val + ' ' + item.currencyCode
                    },
                },
                {
                    title: this.$t('ACCOUNT_CARDS__DEPOSIT'),
                    key: 'editBalance',
                    customTemplate: (item) => {
                        return `
                        <div class="grid__edit-balance deposit">
                            <label>
                                <span>${item.currencyCode}</span>
                                <input type="number" class="general-input required" />
                            </label>
                            <button class="btn deposit">Deposit</button>
                        </div>
                        `
                    },
                    tdClass: 'td-edit-balance',
                    width: '17.5rem',
                },
                {
                    title: this.$t('ACCOUNT_CARDS__WITHDRAW'),
                    key: 'editBalance',
                    customTemplate: (item) => {
                        return `
                        <div class="grid__edit-balance withdraw">
                            <label>
                                <span>${item.currencyCode}</span>
                                <input type="number" class="general-input required" />
                            </label>
                            <button class="btn deposit">Withdraw</button>
                        </div>
                        `
                    },
                    tdClass: 'td-edit-balance',
                    width: '17.5rem',
                },
                {
                    title: this.$t('ACCOUNT_CARDS__LOCKED'),
                    key: 'locked',
                    customTemplate: (item, val) => {
                        return `<div class="switcher lock ${val ? 'active': ''}"></div>`
                    }
                },
                {
                    title: this.$t('ACCOUNT_CARDS__PASSWORD'),
                    key: 'password',
                    customTemplate: (item, val) =>
                        item.firstLoginTime == null ? val : '',
                    dontShowOnHover: true,
                },
                {
                    title: this.$t('ACCOUNT_CARDS__ASSIGNED_TO'),
                    key: 'webShopName',
                    customTemplate: (item, val) => (val ? val : '-'),
                },
                {
                    title: this.$t('ACCOUNT_CARDS__ACTIVE'),
                    key: 'activated',
                    customTemplate: (item, val) => {
                        if (this.isSupervisor) {
                            return val ? 'YES' : 'NO'
                        }
                        return val
                            ? '<span class="activate-success">Activated</span>'
                            : `<div class="switcher activate"></div>`
                    },
                },
                {
                    title: 'Bonus',
                    key: 'hasBonus',
                    customTemplate: (item, val) => 
                        val
                            ? `<div class="btn btn-filter view-bonus">Bonus</div>`
                            : ''
                },          
                {
                    title: '',
                    key: 'actions',
                    customTemplate: item =>
                        item.webShopName
                            ? `<div class="btn btn-filter actions">Actions</div>`
                            : '',
                    tdClass: 'td-actions',
                },
            ]
        },
        columnsWithoutActions() {
            var cols = [...this.columns]
            var actionColumnNames = ['editBalance', 'actions', 'activate', 'locked', 'hasBonus']
            return cols.filter(el => !actionColumnNames.includes(el.key))
        },
        isSupervisor() {
            return this.$store.state.shop
        },
        statusCodes() {
            return this.$store.state.statusCodes
        },
        lastRow() {
            if(this.dataCount == 0 || !this.gridData.length) return []
            return ['Total', this.gridData[0].balanceSum, '', '', '', '', '', '', '', '',]
        },
    },
    watch: {
        createCardFormControls: {
            handler(form) {
                this.checkErrors(form)
            },
            deep: true,
        },
    },
}
</script>

<style scoped lang="scss">
.create-container {
    height: 40 * $rem;
    margin-bottom: 15 * $rem;
    display: flex;

    .btn {
        width: 115 * $rem;
        font-size: 16 * $rem;
        margin-right: 15 * $rem;

        &:last-of-type {
            margin-right: 0;
        }
    }

    .btn.export {
        width: 115 * $rem;
        margin-left: auto;
    }
}

.filter-container {
    display: flex;
    margin-bottom: 15 * $rem;
    background-color: #fff;
    border-radius: 10 * $rem;
    border: 1 * $rem solid #e3e4eb;
    padding: 0 25 * $rem 25 * $rem 25 * $rem;
    flex-wrap: wrap;
    align-items: center;

    label,
    .select-wrapper,
    .checkbox-wrapper,
    .switcher-container,
    .btn {
        margin-right: 15 * $rem;
        margin-top: 25 * $rem;
        width: calc(100% / 8 - #{15 * $rem});
    }
}

.create-form {
    display: flex;
    margin-bottom: 40 * $rem;

    .btn {
        height: 40 * $rem;
        width: 197 * $rem;
        font-size: 16 * $rem;
    }
}

.btn.export {
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    width: 100 * $rem;
    height: 40 * $rem;
}

.filter-show-enter-active,
.filter-show-leave-active {
    transition: opacity 0.2s ease-in-out;
}

.filter-show-enter,
.filter-show-leave-to {
    opacity: 0;
}

/deep/ .grid .btn.btn-filter {
    display: flex;
    justify-content: center;
    min-width: initial;
    width: 80 * $rem;
    min-height: initial;
    height: 34 * $rem;
}

/deep/ .grid__edit-balance {
    height: 34 * $rem;

    input {
        width: 139 * $rem;
        margin-right: 15 * $rem;
        height: 34 * $rem;
    }

    button {
        min-width: initial;
        min-height: initial;
        width: 80 * $rem;
    }

    &.withdraw {
        button {
            background-color: $red;
        }
    }

    label {
        position: relative;

        span {
            position: absolute;
            position: absolute;
            right: 1.5625rem;
            top: 0;
            margin: auto;
            color: #b4b4b4;
        }

        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        input {
            -moz-appearance:textfield; /* Firefox */
        }
    }
}

/deep/ .activate-success {
    color: $green-main;
    font-weight: 600;
    letter-spacing: 0.5 * $rem;
}

/deep/ .td-edit-balance {
    min-width: 280 * $rem;
}

/deep/ .td-actions {
    min-width: 150 * $rem;
}

/deep/ table:first-of-type tr td {
    max-width: none !important;
}

.form-in-filter {
    flex-wrap: wrap;
    margin-top: -25 * $rem;

    label,
    .select-wrapper,
    .checkbox-wrapper,
    .switcher-container,
    .btn {
        margin-top: 25 * $rem;
        width: calc(100% / 8 - #{15 * $rem}) !important;
        margin-right: 15 * $rem;

        &:last-child {
            margin-right: 0;
        }
    }
}

.popup.card-info {
    ul {
        list-style-type: none;
        padding: 0;

        li {
            font-size: 20 * $rem;
            margin-bottom: 10 * $rem;

            span:last-child {
                margin-left: 10 * $rem;
                font-weight: 500;
            }
        }
    }
}

@media only screen and (max-width: 1600px) {
    .filter-container {
        label,
        .select-wrapper,
        .checkbox-wrapper,
        .switcher-container,
        .btn {
            width: calc(25% - #{20 * $rem}) !important;
        }
    }
}

@media only screen and (max-width: 800px) {
    .create-container {
        margin-top: -15 * $rem;
        margin-bottom: 30 * $rem;
        flex-wrap: wrap;
        height: initial;

        .btn:nth-of-type(3n) {
            margin-right: 0;
        }

        .btn.export {
            margin-left: 0;
        }

        .btn,
        .btn.export {
            margin-top: 15 * $rem;
            width: calc(33% - #{10 * $rem});
        }
    }

    .filter-container {
        label,
        .select-wrapper,
        .checkbox-wrapper,
        .switcher-container,
        .btn {
            width: calc(33.33% - #{20 * $rem}) !important;
        }
    }
}

@media only screen and (max-width: 670px) {
    .filter-container {
        label,
        .select-wrapper,
        .checkbox-wrapper,
        .switcher-container,
        .btn {
            width: calc(50% - #{20 * $rem}) !important;
        }
    }
}

@media only screen and (max-width: 340px) {
    .create-container {
        .btn:nth-of-type(3n) {
            margin-right: 15 * $rem;
        }

        .btn:nth-of-type(2n),
        .btn:nth-of-type(6n) {
            margin-right: 0;
        }

        .btn,
        .btn.export {
            margin-top: 15 * $rem;
            width: calc(50% - #{7.5 * $rem});
        }
    }
}
</style>
